import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { activityLog } from "@/features/activityLog";
import { announceTokensPurchased } from "@/shared/lib/tokensPurchased";
import { readTokenReturn } from "../model/returnPath";
import { completeTokenPurchase } from "../model/tokenPurchase";
import cls from "./TokenPurchaseRoot.module.scss";

// Обработанные сессии — на всю вкладку: снятие меток и повторный рендер не
// должны завершать одну оплату дважды.
const handled = new Set<string>();

/** Для тестов: забыть обработанные оплаты. */
export const resetTokenPurchaseRoot = () => handled.clear();

const SHOW_MS = 5000;

/**
 * Возврат со Stripe после покупки токенов — на любой странице (App).
 *
 * Сервер возвращает туда, откуда покупали, с ?tokens_paid=1&session_id=…
 * (урок курса, чат специалиста, «Умный поиск»); старые оформления — на
 * «Умный поиск» с ?payment_success=…. Здесь: убрать метки из адреса, сверить
 * кошелёк (пакет обычно уже записал сервер; нет — дописать тем же id),
 * сказать «Оплата прошла — токены зачислены» и дать странице перечитать баланс.
 */
export const TokenPurchaseRoot = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const [shown, setShown] = useState(false);
    const timer = useRef<number | undefined>(undefined);

    useEffect(() => {
        const ret = readTokenReturn(location.pathname, location.search);
        if (!ret) return;
        // Метки — сразу: обновление страницы не должно их вернуть.
        navigate(
            { pathname: location.pathname, search: ret.search, hash: location.hash },
            { replace: true, state: location.state },
        );
        const sid = ret.sessionId;
        if (!sid || handled.has(sid)) return;
        handled.add(sid);
        void completeTokenPurchase(sid, ret.legacy ? { fallbackPack: { count: 30, days: 30 } } : {})
            .then((result) => {
                activityLog.track("payment_result", {
                    kind: "questions",
                    status: result === "unpaid" ? "pending" : "paid",
                    tokens: result,
                    legacy: ret.legacy,
                });
                if (result !== "credited" && result !== "already") return;
                announceTokensPurchased();
                setShown(true);
                window.clearTimeout(timer.current);
                timer.current = window.setTimeout(() => setShown(false), SHOW_MS);
            })
            .catch(() => undefined);
        // Только смена адреса: navigate и state — не повод начинать заново.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname, location.search]);

    useEffect(() => () => window.clearTimeout(timer.current), []);

    if (!shown) return null;
    return (
        <div className={cls.toast} role="status" aria-live="polite" data-testid="tokens-paid">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M5 12.5l4.5 4.5L19 7.5" />
            </svg>
            <span>{t("tokenPurchase.paid")}</span>
        </div>
    );
};
